import {
  PageContainer,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { Alert, Box, Tabs } from "@mui/material";
import { createFileRoute, Outlet, useMatchRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import { useAuth } from "../../../../../hooks/useAuth";
import { fetchVendor } from "../../../../../services/vendors";
import { VendorProfileBannerWithBadges } from "./-internal_components/VendorProfileBannerWithBadges";

const PATHS_TO_APPLY_LAYOUT = [
  "/vsupplierregistration/vendors/$vendorId",
  "/vsupplierregistration/vendors/$vendorId/users",
  "/vsupplierregistration/vendors/$vendorId/terms",
  "/vsupplierregistration/vendors/$vendorId/terms_exceptions",
  "/vsupplierregistration/vendors/$vendorId/categories",
  "/vsupplierregistration/vendors/$vendorId/documents",
];

const RouteComponentWithLayout: React.FC = () => {
  const vendor = Route.useLoaderData();
  const { user } = useAuth();

  const { t } = useTranslation();
  const matchRoute = useMatchRoute();

  const hasViewCategoryPanelPermission =
    user?.roles_names.includes("Vsupplierregistration - View Category Panel") ||
    user?.vendor?.id === vendor.id;

  const [selectedTab, setSelectedTab] = useState(() => {
    if (matchRoute({ to: "/vsupplierregistration/vendors/$vendorId" })) {
      return "profile";
    }
    if (matchRoute({ to: "/vsupplierregistration/vendors/$vendorId/users" })) {
      return "users";
    }
    if (
      matchRoute({ to: "/vsupplierregistration/vendors/$vendorId/terms" }) ||
      matchRoute({
        to: "/vsupplierregistration/vendors/$vendorId/terms_exceptions",
      })
    ) {
      return "terms";
    }
    if (
      matchRoute({ to: "/vsupplierregistration/vendors/$vendorId/categories" })
    ) {
      return "categories";
    }
    if (
      matchRoute({ to: "/vsupplierregistration/vendors/$vendorId/documents" })
    ) {
      return "documents";
    }
    return "profile";
  });

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      setSelectedTab(newValue);
    },
    [],
  );

  return (
    <PageContainer maxWidth={false} title="Vendor Details">
      {!vendor.active && (
        <Alert severity="error">
          {t("vsupplierregistration.vendors.details.deactivatedVendor")}
        </Alert>
      )}
      {vendor.blocked && !vendor.blocked_by_sap && (
        <Alert severity="warning">
          {t("vsupplierregistration.vendors.details.blockedVendor")}
        </Alert>
      )}
      {!!vendor.blocked_by_sap && (
        <Alert severity="warning">
          {t("vsupplierregistration.vendors.details.blockedVendorBySap")}
        </Alert>
      )}

      <VendorProfileBannerWithBadges vendor={vendor} />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          aria-label="vendor details tabs"
          onChange={handleTabChange}
          value={selectedTab}
        >
          <RouteTabLink
            label={t("vsupplierregistration.vendors.details.tabs.profile")}
            params={{ vendorId: vendor.id.toString() }}
            to="/vsupplierregistration/vendors/$vendorId"
            value="profile"
          />

          <RouteTabLink
            label={t("vsupplierregistration.vendors.details.tabs.users")}
            params={{ vendorId: vendor.id.toString() }}
            to="/vsupplierregistration/vendors/$vendorId/users"
            value="users"
          />

          <RouteTabLink
            label={t("vsupplierregistration.vendors.details.tabs.terms")}
            params={{ vendorId: vendor.id.toString() }}
            to="/vsupplierregistration/vendors/$vendorId/terms"
            value="terms"
          />

          {!!hasViewCategoryPanelPermission && (
            <RouteTabLink
              label={t("vsupplierregistration.vendors.details.tabs.categories")}
              params={{ vendorId: vendor.id.toString() }}
              to="/vsupplierregistration/vendors/$vendorId/categories"
              value="categories"
            />
          )}

          <RouteTabLink
            label={t("vsupplierregistration.vendors.details.tabs.documents")}
            params={{ vendorId: vendor.id.toString() }}
            to="/vsupplierregistration/vendors/$vendorId/documents"
            value="documents"
          />
        </Tabs>
      </Box>

      <Box>
        <Outlet />
      </Box>
    </PageContainer>
  );
};

const RouteComponent: React.FC = () => {
  const matchRoute = useMatchRoute();

  const pathApplyLayout = PATHS_TO_APPLY_LAYOUT.some((path) =>
    matchRoute({ to: path }),
  );

  if (!pathApplyLayout) {
    return <Outlet />;
  }

  return <RouteComponentWithLayout />;
};

export const Route = createFileRoute(
  "/_private/vsupplierregistration/vendors/$vendorId",
)({
  beforeLoad({ context, params }) {
    const user = context.auth.user;

    if (!user) {
      throw new ForbiddenError();
    }

    if (
      user.roles_names.includes("View All Vendors") ||
      user.roles_names.includes("Edit All Vendors") ||
      user.vendor?.id === Number(params.vendorId)
    ) {
      return;
    }

    throw new ForbiddenError();
  },
  component: RouteComponent,
  loader: ({ params: { vendorId } }) => fetchVendor(parseInt(vendorId, 10)),
});
